import type { MessageDto } from '@dijitalapp/shared';
import type { DecryptedContent } from './e2ee';

/** What a notification says of a message's content, in the person's language (push.* texts). */
export interface PreviewTexts {
  newMessage: string;
  photo: string;
  video: string;
  voice: string;
  file: string;
  gif: string;
  sticker: string;
  location: string;
  contact: string;
  poll: string;
  attachment: string;
  /** A file without a name ("📄 Dosya"); `file` when not given. */
  document?: string;
}

/** A notification's preview is cut like the server's (140 UTF-16 units, the last an ellipsis). */
export const PREVIEW_CHARS = 140;
export function cutPreview(text: string): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > PREVIEW_CHARS ? `${clean.slice(0, PREVIEW_CHARS - 1)}…` : clean;
}

const withCaption = (label: string, caption: string | undefined) => (caption?.trim() ? `${label} ${caption.trim()}` : label);

/**
 * What a notification says of a message, as data: made on the sender's device from the decrypted content, sent
 * encrypted for each recipient device (lib/pushPreview.ts) and worded there in the recipient's language
 * (public/sw-preview.js `format`, which must say exactly what formatPreview says).
 *   k: the kind; t: the text, a caption, a poll's question or an event's title; n: a file's or contact's name;
 *   d: a video's or voice message's length in seconds; r: 1 for a reply; ts: when it was made (ms).
 */
export type PreviewKind = 'text' | 'photo' | 'gif' | 'video' | 'voice' | 'sticker' | 'file' | 'location' | 'contact' | 'poll' | 'event';
export interface PreviewData {
  v: 1;
  ts: number;
  k: PreviewKind;
  t?: string;
  n?: string;
  d?: number;
  r?: 1;
}

/** At most `max` UTF-16 units, whitespace collapsed, the last an ellipsis when cut. */
export function clip(text: string | undefined, max: number): string {
  const clean = (text ?? '').replace(/\s+/g, ' ').trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
}

/** The preview of a decrypted message (null: nothing worth telling, e.g. a reaction or an unknown kind). */
export function buildPreview(c: DecryptedContent, opts: { reply?: boolean; now?: number; maxText?: number } = {}): PreviewData | null {
  const max = opts.maxText ?? 100;
  const mime = c.info?.mimetype ?? '';
  const seconds = typeof c.info?.duration === 'number' && c.info.duration > 0 ? Math.round(c.info.duration / 1000) : 0;
  const base = { v: 1 as const, ts: opts.now ?? Date.now(), ...(opts.reply ? { r: 1 as const } : {}) };
  const caption = clip(c.caption, max);
  const withText = (k: PreviewKind, t: string, extra: Partial<PreviewData> = {}): PreviewData => ({ ...base, k, ...(t ? { t } : {}), ...extra });
  switch (c.msgtype) {
    case 'm.text':
    case 'm.notice':
    case 'm.emote': {
      const t = clip(c.body, max);
      return t ? withText('text', t) : null;
    }
    case 'm.sticker':
      return withText('sticker', '');
    case 'm.image':
      if (c.info?.sticker) return withText('sticker', '');
      return withText(mime === 'image/gif' ? 'gif' : 'photo', caption);
    case 'm.video':
      return withText('video', caption, seconds ? { d: seconds } : {});
    case 'm.audio':
      return withText('voice', '', seconds ? { d: seconds } : {});
    case 'm.file': {
      const n = clip(c.body, 80);
      return withText('file', caption, n ? { n } : {});
    }
    case 'm.location':
      return withText('location', '');
    case 'app.dijitalapp.contact': {
      const n = clip(c.contact?.displayName, 80);
      return withText('contact', '', n ? { n } : {});
    }
    case 'app.dijitalapp.poll':
      return withText('poll', clip(c.poll?.question, max));
    case 'app.dijitalapp.event':
      return withText('event', clip(c.event?.title, max));
    default:
      return null;
  }
}

/** m:ss (h:mm:ss from an hour). */
export function formatLength(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const two = (n: number) => String(n).padStart(2, '0');
  const h = Math.floor(s / 3600);
  const m = Math.floor(s / 60) % 60;
  return h > 0 ? `${h}:${two(m)}:${two(s % 60)}` : `${m}:${two(s % 60)}`;
}

/** A preview's line: "🎥 Video (0:12)", "🎤 Sesli mesaj (0:05)", "📄 rapor.pdf", "↪ Tamam"… */
export function formatPreview(p: PreviewData, t: PreviewTexts): string {
  const len = (label: string) => (p.d ? `${label} (${formatLength(p.d)})` : label);
  let line: string;
  switch (p.k) {
    case 'text':
      line = p.t ?? '';
      break;
    case 'photo':
      line = withCaption(t.photo, p.t);
      break;
    case 'gif':
      line = withCaption(t.gif, p.t);
      break;
    case 'video':
      line = withCaption(len(t.video), p.t);
      break;
    case 'voice':
      line = len(t.voice);
      break;
    case 'sticker':
      line = t.sticker;
      break;
    case 'file':
      line = withCaption(p.n ? `📄 ${p.n}` : (t.document ?? t.file), p.t);
      break;
    case 'location':
      line = t.location;
      break;
    case 'contact':
      line = p.n ? `${t.contact} ${p.n}` : t.contact;
      break;
    case 'poll':
      line = `${t.poll} ${p.t ?? ''}`;
      break;
    case 'event':
      line = `📅 ${p.t ?? ''}`;
      break;
    default:
      line = '';
  }
  line = cutPreview(line);
  if (!line) return t.newMessage;
  return p.r ? cutPreview(`↪ ${line}`) : line;
}

/** A decrypted message (end-to-end encrypted chats): "📷 Fotoğraf", "🎤 Sesli mesaj (0:05)", "GIF", the text… */
export function describeDecrypted(c: DecryptedContent, t: PreviewTexts, opts: { reply?: boolean } = {}): string {
  const p = buildPreview(c, { maxText: PREVIEW_CHARS, ...(opts.reply ? { reply: true } : {}) });
  return p ? formatPreview(p, t) : t.newMessage;
}

/** A message the server can read (plaintext chats), as the server's push says it. */
export function describePlain(m: Pick<MessageDto, 'contentType' | 'body' | 'attachment'>, t: PreviewTexts): string {
  const caption = m.body ?? undefined;
  const ms = m.attachment?.durationMs;
  const len = (label: string) => (ms && ms > 0 ? `${label} (${formatLength(ms / 1000)})` : label);
  switch (m.contentType) {
    case 'text':
      return cutPreview(m.body ?? '') || t.newMessage;
    case 'image':
      if (m.attachment?.sticker) return t.sticker;
      return cutPreview(withCaption(m.attachment?.mime === 'image/gif' ? t.gif : t.photo, caption));
    case 'video':
      return cutPreview(withCaption(len(t.video), caption));
    case 'voice':
      return len(t.voice);
    case 'file':
      return cutPreview(withCaption(m.attachment?.name ? `📄 ${m.attachment.name}` : (t.document ?? t.file), caption));
    case 'sticker':
      return t.sticker;
    case 'location':
      return t.location;
    case 'contact':
      return t.contact;
    case 'poll':
      return cutPreview(`${t.poll} ${m.body ?? ''}`);
    default:
      // Never a paperclip "Attachment" for a kind this app does not know: just "New message".
      return t.newMessage;
  }
}
